import paginationService from "../../../../../common/utils/pagination/pagination.service";
import BillingsTaxiOrderListingFilterType from "./types/billings-taxi-order-listing-filter-type";
import BillingsTaxiOrderListingSortKey from "./types/billings-taxi-order-listing-sort-key";
import BillingsTaxiOrderListingFilter, {
  BillingsTaxiOrderListingInternalOrderIdFilter,
  BillingsTaxiOrderListingBillingStatusFilter,
  BillingsTaxiOrderListingOrderStartDateFilter,
  BillingsTaxiOrderListingBillingModelFilter,
  BillingsTaxiOrderListingBillingTypeFilter,
  BillingsTaxiOrderListingCargoCompanyFilter,
  BillingsTaxiOrderListingDestinationTaxiFilter,
  BillingsTaxiOrderListingExcludeCargoCompanyFilter,
  BillingsTaxiOrderListingPlanEntryHumanIdsFilter,
} from "./types/billings-taxi-order-listing-filter";
import { BillingsTaxiOrderListingPassengerFilter } from "./types/billings-taxi-order-listing-filter";
import TaxiCorporationOrderBillingsListParams, {
  TaxiCorporationOrderBillingsListParamsOrder,
} from "../../../../../common/services/taxi-corporation/order-billings/list/taxi-corporation-order-billings-list-params";
import BillingsTaxiOrderListingItemBillingStatus from "./types/billings-taxi-order-listing-item-billing-status";
import BillingsTaxiOrderListingItemBillingType from "./types/billings-taxi-order-listing-item-billing-type";
import BillingStatus from "../../../../../common/types/billing-status";
import TaxiCorporationBillingType from "../../../../../common/types/taxi-corporation-billing-type";

const createStatus = (
  status: BillingsTaxiOrderListingItemBillingStatus | undefined,
): BillingStatus | undefined => {
  switch (status) {
    case BillingsTaxiOrderListingItemBillingStatus.CREATED:
      return BillingStatus.CREATED;
    case BillingsTaxiOrderListingItemBillingStatus.ACCEPTED:
      return BillingStatus.ACCEPTED;
    case BillingsTaxiOrderListingItemBillingStatus.REJECTED:
      return BillingStatus.REJECTED;
    case BillingsTaxiOrderListingItemBillingStatus.REOPENED:
      return BillingStatus.REOPENED;
    case BillingsTaxiOrderListingItemBillingStatus.REOPEN_REQUEST_SENT:
      return BillingStatus.REOPEN_REQUEST;
    default:
      return undefined;
  }
};

const createBillingType = (
  type: BillingsTaxiOrderListingItemBillingType | undefined,
): TaxiCorporationBillingType | undefined => {
  switch (type) {
    case BillingsTaxiOrderListingItemBillingType.PUB_RAILY_2_TAXI:
      return TaxiCorporationBillingType.PUB_RAILY_2_TAXI;
    case BillingsTaxiOrderListingItemBillingType.PRIV_CARGO_2_TAXI:
      return TaxiCorporationBillingType.PRIV_CARGO_2_TAXI;
    case BillingsTaxiOrderListingItemBillingType.PRIV_TAXI_2_RAILY:
      return TaxiCorporationBillingType.PRIV_TAXI_2_RAILY;
    default:
      return undefined;
  }
};

const createRequestOrder = (
  sortKey: BillingsTaxiOrderListingSortKey | null,
): TaxiCorporationOrderBillingsListParamsOrder | undefined => {
  const options: {
    sortKey: BillingsTaxiOrderListingSortKey;
    requestOrder: TaxiCorporationOrderBillingsListParamsOrder;
  }[] = [
    {
      requestOrder: TaxiCorporationOrderBillingsListParamsOrder.DISTANCE_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_DESC,
    },
    {
      requestOrder: TaxiCorporationOrderBillingsListParamsOrder.DISTANCE_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.HIGHWAY_CHARGE_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.TOLL_ROADS_COST_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.HIGHWAY_CHARGE_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.TOLL_ROADS_COST_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.HALTING_AMOUNT_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.STOPOVER_COST_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.HALTING_AMOUNT_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.STOPOVER_COST_DESC,
    },
    {
      requestOrder: TaxiCorporationOrderBillingsListParamsOrder.HUMAN_ID_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.INTERNAL_ORDER_ID_ASC,
    },
    {
      requestOrder: TaxiCorporationOrderBillingsListParamsOrder.HUMAN_ID_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.INTERNAL_ORDER_ID_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.BILLING_MODEL_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.BILLING_MODEL_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.BILLING_MODEL_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.BILLING_MODEL_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.DISTANCE_RATE_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_RATE_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.DISTANCE_RATE_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_RATE_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.DISTANCE_AMOUNT_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_COST_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.DISTANCE_AMOUNT_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_COST_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.TOTAL_AMOUNT_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.TOTAL_COST_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsListParamsOrder.TOTAL_AMOUNT_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.TOTAL_COST_DESC,
    },
  ];

  return options.find((option) => option.sortKey === sortKey)?.requestOrder;
};

const createParams = (
  page: number,
  pageSize: number,
  taxiCorporationUuid: string,
  filters: BillingsTaxiOrderListingFilter[],
  sortKey: BillingsTaxiOrderListingSortKey | null,
): TaxiCorporationOrderBillingsListParams => {
  const offset = paginationService.calculateOffset(page, pageSize);

  return {
    limit: pageSize,
    taxiCorporationUuid,
    offset,
    order: createRequestOrder(sortKey),
    humanId: filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.INTERNAL_ORDER_ID,
    )?.value as
      | BillingsTaxiOrderListingInternalOrderIdFilter["value"]
      | undefined,
    planEntryHumanIds: filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.PLAN_ENTRY_HUMAN_IDS,
    )?.value as
      | BillingsTaxiOrderListingPlanEntryHumanIdsFilter["value"]
      | undefined,
    cargoCompany: filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.CARGO_COMPANY,
    )?.value as BillingsTaxiOrderListingCargoCompanyFilter["value"] | undefined,
    excludeCargoCompany: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiOrderListingFilterType.EXCLUDE_CARGO_COMPANY,
    )?.value as
      | BillingsTaxiOrderListingExcludeCargoCompanyFilter["value"]
      | undefined,
    passenger: filters.find(
      (filter) => filter.type === BillingsTaxiOrderListingFilterType.PASSENGER,
    )?.value as BillingsTaxiOrderListingPassengerFilter["value"] | undefined,
    status: createStatus(
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiOrderListingFilterType.BILLING_STATUS,
      )?.value as
        | BillingsTaxiOrderListingBillingStatusFilter["value"]
        | undefined,
    ),
    dateSince: (
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiOrderListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsTaxiOrderListingOrderStartDateFilter["value"]
        | undefined
    )?.from.toJSON(),
    dateTo: (
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiOrderListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsTaxiOrderListingOrderStartDateFilter["value"]
        | undefined
    )?.to.toJSON(),
    billingModel: filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.BILLING_MODEL,
    )?.value as BillingsTaxiOrderListingBillingModelFilter["value"] | undefined,
    billingType: createBillingType(
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiOrderListingFilterType.BILLING_TYPE,
      )?.value as
        | BillingsTaxiOrderListingBillingTypeFilter["value"]
        | undefined,
    ),
    responsibleTaxi: filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.DESTINATION_TAXI,
    )?.value as
      | BillingsTaxiOrderListingDestinationTaxiFilter["value"]
      | undefined,
  };
};

const billingsTaxiOrderListingRequestFactory = {
  createParams,
};

export default billingsTaxiOrderListingRequestFactory;
